<template>
  <div>
    <slot></slot>
    <canvas @mouseover="mouseOverHandler" @mouseout="mouseOutHandler" @mousemove="mouseMoveHandler" ref="canvas"></canvas>
  </div>
</template>

<script>
import { getCurrentInstance } from "vue";
import { Bounds, CenterScale, Filter, VectorTileModel, defaultTilesLayer } from "vue-niney";
import { SVGConverter } from "./svg-converter";
import { defaultVectorTileCache } from "./vector-tile-cache";

export default {
  name: "NVectorTilesLayer",
  emits: ["hoverFeature"],
  props: {
    layer: { type: Object, default: defaultTilesLayer },
    loader: { type: Object },
    protocol: { type: String },
    tileWidth: { type: Number },
    tileHeight: { type: Number },
    filter: { type: Number },
    filterAndCssFunction: { type: Object }
  },
  data() {
    return {
      boundsModel: null,
      focusModel: null,
      contentChildren: []
    };
  },
  provide() {
    return {
      registerChild: this.registerChild,
      unregisterChild: this.unregisterChild,
      loadTiles: this.loadTiles
    };
  },
  created() {
    this._instance = getCurrentInstance();
    this.childrenFilter = null;
    this.childrenCssFunction = null;
  },
  mounted() {
    this.boundsModel = this.$parent.boundsModel;
    this.focusModel = this.$parent.focusModel;
    this.envelopeModel = this.$parent.envelopeModel;
    
    this.$refs.canvas.width = this.boundsModel.bounds.width;
    this.$refs.canvas.height = this.boundsModel.bounds.height;
    
    this.incubate = true;
    this.vectorTileCache = defaultVectorTileCache;

    this.tileModel = new VectorTileModel();

    const eq = (a, b) => (a.length == b.length) && (a.every((aa, i) => aa == b[i]));
    this.tileModel.tileNeedsReload = tile => {
      return (
        (tile.symbology == null) ||
        !eq(tile.symbology.filters, this.contentChildren.map(symbolizer => symbolizer.mapFeatureModel.filter)) ||
        !eq(tile.symbology.cssFunctions, this.contentChildren.map(symbolizer => symbolizer.mapFeatureModel.cssFunction))
      );
    };
    this.tileModel.loadTileData = tile => {
      tile.symbology = {
        filters: this.contentChildren.map(symbolizer => symbolizer.mapFeatureModel.filter),
        cssFunctions: this.contentChildren.map(symbolizer => symbolizer.mapFeatureModel.cssFunction)
      };
      this.vectorTileCache.loadTileData(tile, this.layer.name, this);
    };

    this.tileModel.ctx = this.$refs.canvas.getContext("2d");
    this.tileModel.srs = this.focusModel.srs;
    this.tileModel.maxEnvelope = this.focusModel.maxEnvelope;
    this.tileModel.loader = this.loader;
    if (this.protocol != null) {
      this.tileModel.protocol = this.protocol;
    }
    if (this.tileWidth != null) {
      this.tileModel.tileWidth = parseInt(this.tileWidth);
    }
    if (this.tileHeight != null) {
      this.tileModel.tileHeight = parseInt(this.tileHeight);
    }
    if (!this.incubate) {
        this.tileModel.setBoundsAndCenterScales(this.boundsModel.bounds, this.focusModel.centerScale, this.focusModel.animationCenterScale, this.envelopeModel.getEnvelope(), this.envelopeModel.getAnimationEnvelope());
    } else {
        this.tileModel.setBoundsAndCenterScales(this.boundsModel.bounds, this.focusModel.incubationCenterScale, this.focusModel.animationCenterScale, this.envelopeModel.getIncubationEnvelope(), this.envelopeModel.getAnimationEnvelope());
    }

    this.tileModel.setLayer(this.layer);

    this.setChildrenFilter();
  },
  watch: {
    "$parent.boundsModel": function(val) {
      this.boundsModel = val;
    },
    "$parent.focusModel": function(val) {
      this.focusModel = val;
    },
    "$parent.envelopeModel": function(val) {
      this.envelopeModel = val;
    },
    "boundsModel.bounds": function() {
      this.$refs.canvas.width = this.boundsModel.bounds.width;
      this.$refs.canvas.height = this.boundsModel.bounds.height;

      if (!this.incubate) {
        this.tileModel.setBoundsAndCenterScales(this.boundsModel.bounds, this.focusModel.centerScale, this.focusModel.animationCenterScale, this.envelopeModel.getEnvelope(), this.envelopeModel.getAnimationEnvelope());
      } else {
        this.tileModel.setBoundsAndCenterScales(this.boundsModel.bounds, this.focusModel.incubationCenterScale, this.focusModel.animationCenterScale, this.envelopeModel.getIncubationEnvelope(), this.envelopeModel.getAnimationEnvelope());
        this.tileModel.resetTiles();
      }
    },
    "focusModel.centerScale": function() {
      if (!this.incubate) {
        this.tileModel.setBoundsAndCenterScales(this.boundsModel.bounds, this.focusModel.centerScale, this.focusModel.animationCenterScale, this.envelopeModel.getEnvelope(), this.envelopeModel.getAnimationEnvelope());
      }
    },
    "focusModel.animationCenterScale": function() {
      if (!this.incubate) {
        this.tileModel.setBoundsAndCenterScales(this.boundsModel.bounds, this.focusModel.centerScale, this.focusModel.animationCenterScale, this.envelopeModel.getEnvelope(), this.envelopeModel.getAnimationEnvelope());
      } else {
        this.tileModel.setBoundsAndCenterScales(this.boundsModel.bounds, this.focusModel.incubationCenterScale, this.focusModel.animationCenterScale, this.envelopeModel.getIncubationEnvelope(), this.envelopeModel.getAnimationEnvelope());
        this.tileModel.resetTiles();

        this.hover();
      }
    },
    "focusModel.incubationCenterScale": function() {
      if (this.incubate) {
        this.tileModel.setBoundsAndCenterScales(this.boundsModel.bounds, this.focusModel.incubationCenterScale, this.focusModel.animationCenterScale, this.envelopeModel.getIncubationEnvelope(), this.envelopeModel.getAnimationEnvelope());
        this.tileModel.resetTiles();
      }
    },
    layer: function() {
      this.tileModel.setLayer(this.layer);
    },
    "layer.visible": function(val) {
      if (val) {
        this.tileModel.loadTiles();
      }
      this.tileModel.resetTiles();
    },
    loader: function(val) {
      this.tileModel.loader = this.loader;
    },
    protocol: function(val) {
      if (val != null) {
        this.tileModel.protocol = val;
      }
    },
    tileWidth: function(val) {
      if (val != null) {
        this.tileModel.tileWidth = parseInt(val);
      }
    },
    tileHeight: function(val) {
      if (val != null) {
        this.tileModel.tileHeight = parseInt(val);
      }
    },
    filter: function() {
      this.setChildrenFilter();
      this.tileModel.loadTiles();
    },
    filterAndCssFunction: function() {
      this.setChildrenFilter();
      this.tileModel.loadTiles();
    }

  },
  methods: {
    mouseOverHandler(mouseEvent) {
      this.mouseEvent = mouseEvent;
      this.hover();
    },
    mouseMoveHandler(mouseEvent) {
      this.mouseEvent = mouseEvent;
      this.hover();
    },
    mouseOutHandler() {
      this.mouseEvent = null;
      this.hover();
    },
    hover() {
      const hasListener = !!this._instance.vnode?.props?.onHoverFeature;
      if (!hasListener) {
        return;
      }
      if (this.mouseEvent == null) {
        this.$emit("hoverFeature", null, null);
        return;
      }

      const srs = this.focusModel.srs;
      const centerScale = this.focusModel.animationCenterScale;
      const bounds = this.boundsModel.bounds;
      const offsetX = this.mouseEvent.offsetX;
      const offsetY = this.mouseEvent.offsetY;
      const worldX = centerScale.getWorldX(bounds.width, offsetX);
      const worldY = centerScale.getWorldY(bounds.height, offsetY);
      const zoomLevel = srs.getZoomLevel(centerScale.scale, this.tileModel.maxZoomLevel);
      const tileX = Math.floor((worldX - srs.minX) / zoomLevel.resolution / this.tileModel.tileWidth);
      const tileY = Math.max(Math.floor((srs.maxY - worldY) / zoomLevel.resolution / this.tileModel.tileHeight), 0);
      const tile = this.tileModel.getTile(zoomLevel.zoomLevel, tileX, tileY);
      if ((tile == null) || (tile.features == null)) {
        this.$emit("hoverFeature", null, null);
        return;
      }

      const svgConverter = new SVGConverter();
      const mouseX = (offsetX - tile.x) / 256 / tile.scaling * 4096;
      const mouseY = (offsetY - tile.y) / 256 / tile.scaling * 4096;
      for (let i = 0; i < tile.features.length; i++) {
        const feature = tile.features[i];
        if (
          (mouseX >= feature.properties.minx) &&
          (mouseX <= feature.properties.maxx) &&
          (mouseY >= feature.properties.miny) &&
          (mouseY <= feature.properties.maxy) &&
          (svgConverter.isPointInPath(mouseX, mouseY, feature.geometry))
        ) {
          this.$emit("hoverFeature", tile, feature);
          return;
        }
      }
      this.$emit("hoverFeature", null, null);
    },


  loadTileDataHandler(tile, vectorTileLayers, transform, discardWithSuccess = false) {
    if (vectorTileLayers == null) {
      this.discardTileData(tile, discardWithSuccess);
    } else if ((vectorTileLayers.reduce((num, vectorTileLayer) => num += vectorTileLayer.length, 0) < 1000) && ((this.childrenFilter == null) || (this.childrenFilter.value.length < 3000))) {
      this.processTileData(tile, vectorTileLayers, transform.zoomFactor, transform.xShiftFactor, transform.yShiftFactor);
    } else {
      setTimeout(() => this.processTileData(tile, vectorTileLayers, transform.zoomFactor, transform.xShiftFactor, transform.yShiftFactor));
    }
  },

  discardTileData(tile, success) {
    if (this.tileModel.loader != null) {
      this.tileModel.loader.subtract(this.tileModel.layer.name);
    }
    tile.completed = true;

    if (success) {
      tile.corrupted = performance.now() - 7000;
    } else {
      tile.corrupted = performance.now();
      
      console.log("Error loading tile: " + tile.url);
    }
  },

  processTileData(tile, vectorTileLayers, zoomFactor, xShiftFactor, yShiftFactor) {
    if (tile.tileWidth != tile.tileHeight) {
      console.warn("Tile width and height are not equal. Image will be distorted.");
    }

    let filterHash = {};
    if (this.childrenFilter != null) {
      this.childrenFilter.value.forEach(filterValue => filterHash[filterValue] = true);
    } else if (this.contentChildren.every(contentChild => contentChild.filter)) {
      this.contentChildren.forEach(
        contentChild => contentChild.filter.value.forEach(
          filterValue => filterHash[filterValue] = true
        )
      );
    } else {
      filterHash = null;
    }

    const svgConverter = new SVGConverter();
    const features = [];
    const pointFeatures = [];

    for (let vectorTileLayer of vectorTileLayers) {
      for (let i = 0; i < vectorTileLayer.length; i++) {
        const feature = vectorTileLayer.feature(i);
        if ((filterHash == null) || filterHash[feature.properties.id]) {  // Hardcoded id.
          if (!vectorTileLayer["pointsOnly"]) {
            feature.geometry = svgConverter.vectorTileFeatureToPath(feature);
            features.push(feature);
          } else {
            feature.geometry = svgConverter.vectorTileFeatureToPoint(feature);
            pointFeatures.push(feature);
          }
        }
      }
    }

    const canvas = this.createCanvas(tile.tileWidth, tile.tileHeight);
    const pointCanvas = this.createCanvas(tile.tileWidth + 2 * this.tileModel.snoopMargin, tile.tileHeight + 2 * this.tileModel.snoopMargin);
    const unscaledExtent = vectorTileLayers[0].extent;
    const extent = unscaledExtent / zoomFactor;
    const bounds = new Bounds(canvas.width, canvas.height);
    const pointBounds = new Bounds(pointCanvas.width, pointCanvas.height);
    const centerScale = new CenterScale(xShiftFactor * unscaledExtent + extent / 2, yShiftFactor * unscaledExtent + extent / 2, extent / 0.000352778 / tile.tileWidth, 1);

    this.contentChildren.forEach(contentChild => {
      contentChild.setContext(canvas.getContext("2d"), false, bounds, centerScale, features);
      contentChild.setContext(pointCanvas.getContext("2d"), true, pointBounds, centerScale, pointFeatures);
    });

    tile.data = canvas;
    tile.snoopData = pointCanvas;
    tile.features = features;
    tile.pointFeatures = pointFeatures;

    this.tileModel.completeTile(tile, true);

    this.hover();
  },

  createCanvas(width, height) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    return canvas;
  },


  setChildrenFilter() {
    if ((this.filter == null) && (this.filterAndCssFunction == null)) {
      return;
    }

    const filter = this.filter || this.filterAndCssFunction.filter;
    const filterValues = Array.isArray(filter) ? filter: [filter];
    if (
      (this.childrenFilter != null) &&
      (this.childrenFilter.value.length == filterValues.length) &&
      (this.childrenFilter.value.every((filterValue, i) => filterValue == filterValues[i]))
    ) {
      return;
    }

    this.childrenFilter = new Filter("properties.id", filterValues, "IN");

    this.contentChildren.forEach(contentChild => {
      contentChild.mapFeatureModel.filter = this.childrenFilter;
    });
  },


    registerChild(child) {
      this.contentChildren.push(child);
    },
    unregisterChild(child) {
      this.contentChildren = this.contentChildren.filter(c => c !== child);
    },


    loadTiles() {
      this.tileModel.loadTiles();
    }



  }
}
</script>

<style scoped>
canvas {
  display: block;
  position: absolute;
  width: 100%;
  height: 100%;
}
</style>
